import React, { useState } from "react";
import { StandardURL } from "../App";

// Helper component to render code blocks with a copy button
const CopyableCodeBlock = ({ title, code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border)', marginTop: '1rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#27272a', padding: '0.6rem 1.5rem', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{title}</span>
        <button 
          onClick={handleCopy} 
          style={{ 
            background: copied ? 'var(--success)' : 'rgba(255,255,255,0.05)', 
            color: copied ? '#000' : 'var(--text-main)', 
            border: copied ? '1px solid var(--success)' : '1px solid var(--border)', 
            borderRadius: '6px', padding: '4px 12px', fontSize: '0.75rem', cursor: 'pointer',
            transition: 'all 0.2s ease', fontWeight: 600
          }}
        >
          {copied ? "Copied!" : "Copy Code"}
        </button>
      </div>
      <pre style={{ margin: 0, background: '#09090b', padding: '1.5rem', color: '#38bdf8', fontSize: '0.85rem', overflowX: 'auto', lineHeight: '1.6' }}>
{code}
      </pre>
    </div>
  );
};

const DeveloperDocsPage = ({ vid }) => {

  // Code Strings
  const curlSingle = `curl -X POST ${StandardURL}/sms/api/send \\
-H "Content-Type: application/json" \\
-d '{
  "message": "Transactional notification message string.",
  "vid": "${vid}",
  "hashkey": "YOUR_API_HASH_KEY",
  "webhookstatus": 1,
  "phones": ["2547XXXXXXXX"]
}'`;

  const phpSingle = `<?php
$url = "https://api.multiphasicinfraconstruct.co.ke/sms/sms/api/send";

$payload = [
    "message"       => "Hello testing the live URL!",
    "vid"           => "${vid}", // Auto-filled from dashboard
    "hashkey"       => "", // Get from Security Tab
    "webhookstatus" => 1, // Use 0 if you dont want a webhook sent. Use 1 if you want a webhook sent
    "phones"        => ["254700000000"] // Sent as an array
];

$ch = curl_init($url);

curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json'
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);

if (curl_errno($ch)) {
    $error = curl_error($ch);
    curl_close($ch);
    die("cURL Transport Error: " . $error);
}

curl_close($ch);

echo "HTTP Status Code: " . $httpCode . "\\n";
echo "Response Payload:\\n";
print_r(json_decode($response, true) ?? $response);`;

  const curlBatch = `curl -X POST ${StandardURL}/sms/api/send-batch \\
-H "Content-Type: application/json" \\
-d '{
  "message": "Mass marketing communication alert broadcast content payload.",
  "vid": "${vid}",
  "hashkey": "YOUR_API_HASH_KEY",
  "webhookstatus": 0,
  "phones": ["2547XXXXXXXX", "2547YYYYYYYY"]
}'`;

  const phpBatch = `<?php
$url = "https://api.multiphasicinfraconstruct.co.ke/sms/sms/api/send-batch";

$payload = [
    "message"       => "Hello testing the live URL!",
    "vid"           => "${vid}", // Auto-filled from dashboard
    "hashkey"       => "", // Get from Security Tab
    "webhookstatus" => 1, // Use 0 if you dont want a webhook sent. Use 1 if you want a webhook sent
    "phones"        => ["254700000000","254700000000"] // Sent as an array list wrapper
];

$ch = curl_init($url);

curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json'
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);

if (curl_errno($ch)) {
    $error = curl_error($ch);
    curl_close($ch);
    die("cURL Transport Error: " . $error);
}

curl_close($ch);

echo "HTTP Status Code: " . $httpCode . "\\n";
echo "Response Payload:\\n";
print_r(json_decode($response, true) ?? $response);`;

  const successResponse = `{
  "status": "success",
  "message": "Batch processed successfully",
  "data": {
    "total": 2,
    "successful": 2,
    "failed": 0,
    "remaining_credits": 1420.50,
    "results": [
      { "phone": "2547XXXXXXXX", "status": "success" },
      { "phone": "2547YYYYYYYY", "status": "success" }
    ]
  }
}`;

  return (
    <div className="view-container">
      <div className="page-header" style={{ marginBottom: '3rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800 }}>API Documentation</h1>
        <p style={{ color: 'var(--text-muted)' }}>RESTful engine specifications for dynamic system integrations.</p>
      </div>

      <div className="card" style={{ maxWidth: '900px' }}>
        
        {/* Endpoint 1: Single SMS API */}
        <div style={{ marginBottom: '4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <span style={{ background: 'var(--success)', color: '#000', padding: '4px 12px', borderRadius: '6px', fontWeight: 900, fontSize: '0.75rem' }}>POST</span>
            <code style={{ fontSize: '1.1rem', color: '#fff', fontWeight: 600 }}>{StandardURL}/sms/api/send</code>
          </div>
          <p style={{ color: 'var(--text-muted)', lineHeight: '1.6', marginBottom: '1.5rem' }}>
            Submit a high-priority transactional SMS payload targeting a single destination node.
          </p>
          
          <div className="form-label" style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Payload Fields Matrix</div>
          <ul style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
            <li><strong>message:</strong> String message body content block text string.</li>
            <li><strong>vid:</strong> Core unique Vendor ID record parameter visible on dashboard overview.</li>
            <li><strong>hashkey:</strong> Secure API Integration key generated in dashboard security tab.</li>
            <li><strong>webhookstatus:</strong> Integer flag mapping rules (<code>1</code> triggers callback hooks, <code>0</code> disables routing callbacks).</li>
            <li><strong>phones:</strong> Array tracking array mapping context holding the target number string.</li>
          </ul>

          <CopyableCodeBlock title="cURL Execution Frame" code={curlSingle} />
          <CopyableCodeBlock title="PHP Integration" code={phpSingle} />
        </div>

        {/* Endpoint 2: Batch SMS API */}
        <div style={{ marginBottom: '4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <span style={{ background: 'var(--success)', color: '#000', padding: '4px 12px', borderRadius: '6px', fontWeight: 900, fontSize: '0.75rem' }}>POST</span>
            <code style={{ fontSize: '1.1rem', color: '#fff', fontWeight: 600 }}>{StandardURL}/sms/api/send-batch</code>
          </div>
          <p style={{ color: 'var(--text-muted)', lineHeight: '1.6', marginBottom: '1.5rem' }}>
            Deploys automated large mass vector communication broadcasts targeting multiple mobile devices.
          </p>

          <div className="form-label" style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Payload Fields Matrix</div>
          <ul style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
            <li><strong>phones:</strong> Array tracking list containing multiple cellular phone numbers <code>["2547XXXXXXXX", "2547YYYYYYYY"]</code>.</li>
            <li><strong>message</strong>, <strong>vid</strong>, <strong>hashkey</strong>, and <strong>webhookstatus</strong> specifications match the single transaction nodes rules defined above.</li>
          </ul>
          
          <CopyableCodeBlock title="cURL Execution Frame" code={curlBatch} />
          <CopyableCodeBlock title="PHP Integration" code={phpBatch} />
        </div>

        {/* Response Blocks Matrix */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '1.5rem' }}>Infrastructure Response Specs</h2>
          
          <div style={{ marginBottom: '2rem' }}>
            <div className="form-label" style={{ color: '#10b981', marginBottom: '8px' }}>Success payload scheme structure (200 OK)</div>
            <CopyableCodeBlock title="JSON Response" code={successResponse} />
          </div>

          <div>
            <div className="form-label" style={{ color: '#ef4444', marginBottom: '8px' }}>Error response status map tracking matrices (400 Bad Request)</div>
            <div style={{ background: '#111827', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <ul style={{ color: 'var(--text-muted)', fontSize: '0.85rem', listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '8px' }}>• <code>Invalid VID.</code></li>
                <li style={{ marginBottom: '8px' }}>• <code>Invalid credentials.</code></li>
                <li style={{ marginBottom: '8px' }}>• <code>VID is inactive.</code></li>
                <li style={{ marginBottom: '8px' }}>• <code>Message flagged for inappropriate content.</code></li>
                <li style={{ marginBottom: '8px' }}>• <code>No recipients provided.</code></li>
                <li>• <code>Sorry insufficient credits kindly top up.</code></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperDocsPage;